import React, { useState, useEffect, useCallback } from "react";
import {
  GripVertical,
  Edit2,
  Eye,
  EyeOff,
  Check,
  X,
  Loader2,
  Layers,
  Plus,
  Save,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GetAxios,
  PostAxios,
  PatchAxios,
  DeleteAxios,
} from "../../helpers/axiosHelpers";
import { toast } from "sonner";

// ─── Predefined section slugs per page ───────────────────────────────────────
const PREDEFINED = {
  landing: [
    { slug: "hero",            display: "Hero Banner" },
    { slug: "about",           display: "About Us" },
    { slug: "company-info",    display: "Company Info" },
    { slug: "our-services",    display: "Our Services / Mission" },
    { slug: "benefits",        display: "Why Choose Us (Benefits)" },
    { slug: "candidate-cta",   display: "Candidate CTA Band" },
    { slug: "testimonials",    display: "Testimonials" },
    { slug: "contact",         display: "Contact Us" },
    { slug: "map",             display: "Location Map" },
  ],
  apply: [
    { slug: "job-seeker-hero", display: "Job Seeker Hero Banner" },
    { slug: "job-seeker-stats", display: "Job Seeker Stats Bar" },
    { slug: "register-form", display: "Application Registration Form" },
  ],
};

// ─── Empty form skeleton ──────────────────────────────────────────────────────
const EMPTY_FORM = {
  labelEn: "", labelJa: "",
  titleEn: "", titleJa: "",
  subtitleEn: "", subtitleJa: "",
};

// ─── SectionEditModal ─────────────────────────────────────────────────────────
const SectionEditModal = ({ section, slug, page, onClose, onSaved }) => {
  const isCreate = !section;
  const [form, setForm] = useState(
    isCreate
      ? EMPTY_FORM
      : {
          labelEn: section.labelEn || "",
          labelJa: section.labelJa || "",
          titleEn: section.titleEn || "",
          titleJa: section.titleJa || "",
          subtitleEn: section.subtitleEn || "",
          subtitleJa: section.subtitleJa || "",
        },
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      let res;
      if (isCreate) {
        res = await PostAxios("/page-sections", { slug, page, ...form });
      } else {
        res = await PatchAxios(`/page-sections/${section.id}`, form);
      }
      if (res?.success) {
        toast.success(isCreate ? "Section created!" : "Section updated!");
        onSaved();
        onClose();
      } else {
        toast.error(res?.msg || "Save failed");
      }
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, name, isTextarea }) => (
    <div>
      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
        {label}
      </label>
      {isTextarea ? (
        <textarea
          rows={2}
          value={form[name]}
          onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
          className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-subidha-blue/20 resize-none"
          placeholder={`Enter ${label.toLowerCase()}...`}
        />
      ) : (
        <input
          type="text"
          value={form[name]}
          onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
          className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-subidha-blue/20"
          placeholder={`Enter ${label.toLowerCase()}...`}
        />
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-subidha-blue rounded-xl flex items-center justify-center">
              <Layers className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-black text-slate-900">
                {isCreate ? "Customize Section" : "Edit Section"}
              </h2>
              <p className="text-[11px] text-slate-400 font-mono">{slug}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Section Label (EN)" name="labelEn" />
            <Field label="Section Label (JA)" name="labelJa" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Title (EN)" name="titleEn" />
            <Field label="Title (JA)" name="titleJa" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Subtitle (EN)" name="subtitleEn" isTextarea />
            <Field label="Subtitle (JA)" name="subtitleJa" isTextarea />
          </div>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            Leave fields empty to keep using the default locale text.
          </p>
        </div>

        <div className="flex gap-3 p-6 border-t border-slate-100">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-3 rounded-xl bg-subidha-blue text-white text-sm font-black flex items-center justify-center gap-2 hover:bg-slate-900 transition-all disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isCreate ? "Create Section" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── SortableRow (DB-backed, draggable) ───────────────────────────────────────
const SortableRow = ({ section, onEdit, onToggle, toggling }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-4 p-4 bg-white rounded-2xl border ${
        section.isActive ? "border-slate-200" : "border-slate-100 opacity-60"
      } shadow-sm hover:shadow-md transition-all`}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing touch-none shrink-0"
      >
        <GripVertical className="w-5 h-5" />
      </button>

      {/* Section info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${
              section.isActive
                ? "bg-green-100 text-green-700"
                : "bg-slate-100 text-slate-500"
            }`}
          >
            {section.isActive ? "Active" : "Hidden"}
          </span>
          <span className="text-[11px] font-mono text-slate-400 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full">
            {section.slug}
          </span>
        </div>
        {section.titleEn && (
          <p className="text-sm font-semibold text-slate-700 mt-1 truncate">
            {section.titleEn}
          </p>
        )}
        {section.labelEn && (
          <p className="text-[11px] text-accent-green font-bold uppercase tracking-wider mt-0.5">
            {section.labelEn}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={() => onToggle(section)}
          disabled={toggling === section.id}
          className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-all disabled:opacity-40"
          title={section.isActive ? "Hide section" : "Show section"}
        >
          {toggling === section.id ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : section.isActive ? (
            <Eye className="w-4 h-4" />
          ) : (
            <EyeOff className="w-4 h-4" />
          )}
        </button>
        <button
          onClick={() => onEdit(section)}
          className="p-2 rounded-xl text-slate-400 hover:bg-subidha-blue/10 hover:text-subidha-blue transition-all"
          title="Edit section content"
        >
          <Edit2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ─── UncustomizedRow (not yet in DB) ─────────────────────────────────────────
const UncustomizedRow = ({ slug, display, onCreate }) => (
  <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
    <GripVertical className="w-5 h-5 text-slate-200 shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-100 text-slate-400">
          Locale Only
        </span>
        <span className="text-[11px] font-mono text-slate-400 bg-white border border-slate-200 px-2 py-0.5 rounded-full">
          {slug}
        </span>
      </div>
      <p className="text-xs text-slate-400 mt-1">{display} — using locale fallback</p>
    </div>
    <button
      onClick={() => onCreate(slug)}
      className="shrink-0 flex items-center gap-1.5 bg-subidha-blue/10 text-subidha-blue hover:bg-subidha-blue hover:text-white text-[11px] font-black uppercase tracking-wider px-3 py-1.5 rounded-xl transition-all"
    >
      <Plus className="w-3.5 h-3.5" />
      Customize
    </button>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const PageSectionsManager = () => {
  const [activePage, setActivePage] = useState("landing");
  const [allSections, setAllSections] = useState([]);
  const [orderedDbItems, setOrderedDbItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(null);
  const [editTarget, setEditTarget] = useState(null); // { section (or null), slug, page }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const fetchSections = useCallback(async () => {
    setLoading(true);
    try {
      const res = await GetAxios("/page-sections");
      if (res?.success && Array.isArray(res.data)) {
        setAllSections(res.data);
      }
    } catch {
      toast.error("Failed to load sections");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  // Rebuild ordered list whenever page tab or allSections changes
  useEffect(() => {
    const dbForPage = allSections
      .filter((s) => s.page === activePage)
      .sort((a, b) => a.displayOrder - b.displayOrder);
    setOrderedDbItems(dbForPage);
    setDirty(false);
  }, [activePage, allSections]);

  // Slugs not yet in DB for this page
  const predefined = PREDEFINED[activePage] || [];
  const dbSlugs = new Set(orderedDbItems.map((s) => s.slug));
  const uncustomized = predefined.filter((d) => !dbSlugs.has(d.slug));

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setOrderedDbItems((items) => {
      const oldIdx = items.findIndex((i) => i.id === active.id);
      const newIdx = items.findIndex((i) => i.id === over.id);
      setDirty(true);
      return arrayMove(items, oldIdx, newIdx);
    });
  };

  const handleSaveOrder = async () => {
    setSaving(true);
    try {
      const res = await PatchAxios("/page-sections/reorder", {
        orderedIds: orderedDbItems.map((s) => s.id),
      });
      if (res?.success) {
        toast.success("Order saved!");
        setDirty(false);
      } else {
        toast.error(res?.msg || "Failed to save order");
      }
    } catch {
      toast.error("Failed to save order");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (section) => {
    setToggling(section.id);
    try {
      const res = await PatchAxios(`/page-sections/${section.id}`, {
        isActive: !section.isActive,
      });
      if (res?.success) {
        setAllSections((prev) =>
          prev.map((s) =>
            s.id === section.id ? { ...s, isActive: !s.isActive } : s,
          ),
        );
        toast.success(section.isActive ? "Section hidden" : "Section visible");
      } else {
        toast.error("Toggle failed");
      }
    } catch {
      toast.error("Toggle failed");
    } finally {
      setToggling(null);
    }
  };

  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center opacity-30">
        <Loader2 className="w-8 h-8 animate-spin text-subidha-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Tab Switcher */}
      <div className="bg-white rounded-[2rem] shadow-xl border border-slate-100 p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-lg font-black text-slate-900 mb-1">Page Sections</h2>
            <p className="text-xs text-slate-500">
              Drag to reorder · Toggle visibility · Customize header text
            </p>
          </div>
          <div className="flex gap-2">
            {["landing", "apply"].map((p) => (
              <button
                key={p}
                onClick={() => setActivePage(p)}
                className={`px-5 py-2.5 rounded-xl text-sm font-black transition-all capitalize ${
                  activePage === p
                    ? "bg-subidha-blue text-white shadow-lg shadow-blue-500/20"
                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
              >
                {p === "landing" ? "Landing Page" : "Apply Page"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Section List */}
      <div className="bg-white rounded-[2rem] shadow-xl border border-slate-100 p-6">
        <div className="space-y-3">
          {/* Draggable DB sections */}
          {orderedDbItems.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={orderedDbItems.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                {orderedDbItems.map((section) => (
                  <SortableRow
                    key={section.id}
                    section={section}
                    onEdit={(s) =>
                      setEditTarget({ section: s, slug: s.slug, page: activePage })
                    }
                    onToggle={handleToggle}
                    toggling={toggling}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            <p className="text-center text-sm text-slate-400 py-4">
              No customized sections yet. Click "Customize" below to get started.
            </p>
          )}

          {/* Non-DB sections */}
          {uncustomized.map((def) => (
            <UncustomizedRow
              key={def.slug}
              slug={def.slug}
              display={def.display}
              onCreate={(slug) =>
                setEditTarget({ section: null, slug, page: activePage })
              }
            />
          ))}
        </div>

        {/* Save Order Button */}
        {dirty && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleSaveOrder}
              disabled={saving}
              className="flex items-center gap-2 bg-accent-green text-white font-black text-sm px-6 py-3 rounded-xl hover:bg-[#66b32e] transition-all disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Save Order
            </button>
          </div>
        )}
      </div>

      {/* Edit / Create Modal */}
      {editTarget && (
        <SectionEditModal
          section={editTarget.section}
          slug={editTarget.slug}
          page={editTarget.page}
          onClose={() => setEditTarget(null)}
          onSaved={fetchSections}
        />
      )}
    </div>
  );
};

export default PageSectionsManager;
